import React, { useCallback, useEffect, useState } from 'react';
import { FiEdit2, FiExternalLink, FiGitBranch, FiMapPin, FiMic, FiPlus, FiRotateCcw, FiSave, FiX } from 'react-icons/fi';
import {
  ContactIdentitiesInfo,
  ContactOpportunity,
  ContactOriginInfo,
  FieldChange,
  createOpportunity,
  getContactIdentities,
  getContactOrigin,
  listContactOpportunities,
  listFieldChanges,
  setOpportunityTemperature,
  updateOpportunity,
} from '../../services/CardService';
import { TEMPERATURE_LABELS, normalizeTemperature, type Temperature } from '../../services/revenueTypes';
import TemperatureChip from '../../mendsAi/components/revenue/TemperatureChip';
import { openRegisterConversation } from '../../mendsAi/openRegister';
import ContactIdentityList from './ContactIdentityList';
import LeadFormAnswers from './LeadFormAnswers';
import './crmFunnel.css';

export interface OpportunityPanelColumn {
  id: string;
  title: string;
  kind?: string | null;
}

interface Props {
  sectorId: number;
  contactId: number;
  /** Card aberto na ficha (a oportunidade em foco). */
  cardId: number;
  columns?: OpportunityPanelColumn[];
  onChanged?: () => void;
  /** Abre a ficha completa da oportunidade. Opcional: sem ela o botão "Abrir ficha" não aparece. */
  onOpenDossier?: (cardId: number) => void;
  addToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

const FIELD_LABELS: Record<string, string> = {
  title: 'Assunto',
  estimated_value: 'Valor',
  temperature: 'Temperatura',
  column: 'Etapa',
  name: 'Nome',
  email: 'E-mail',
  number: 'Telefone',
  notes: 'Observações',
};

/** Valor do histórico como a ficha mostra: "2500.00 BRL" vira "R$ 2.500". Outros campos ficam como gravados. */
export function historyValue(field: string, raw: string | null | undefined): string {
  if (raw == null || raw === '') return '(vazio)';
  if (field === 'temperature') {
    const t = normalizeTemperature(raw);
    return t ? TEMPERATURE_LABELS[t] : raw;
  }
  if (field !== 'estimated_value') return raw;
  const [amount, currency] = raw.trim().split(/\s+/);
  const n = Number(amount);
  return Number.isFinite(n) ? formatMoney(n, currency ?? null) : raw;
}

const CREATOR_LABELS: Record<string, string> = {
  ai: 'aberta pela IA',
  user: 'aberta por uma pessoa',
  registrar: 'aberta pelo Registrar',
  system: 'entrada automática',
};

export function formatMoney(value: number | null | undefined, currency: string | null | undefined): string {
  if (value == null) return 'A definir';
  const code = currency === 'USD' || currency === 'EUR' || currency === 'BRL' ? currency : null;
  try {
    return code
      ? new Intl.NumberFormat(code === 'BRL' ? 'pt-BR' : 'en-US', { style: 'currency', currency: code, maximumFractionDigits: 0 }).format(value)
      : new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(value);
  } catch {
    return String(value);
  }
}

function formatWhen(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function statusLabel(o: ContactOpportunity): string {
  if (o.isEntry) return 'Contato (sem oportunidade ainda)';
  if (o.isClosed) return o.columnKind === 'ganho' ? 'Ganha' : 'Perdida';
  return 'Em andamento';
}

/**
 * Ficha do card no funil único: origem original × canal atual × canais conhecidos, as oportunidades do cliente
 * (card = oportunidade; cada uma com etapa, valor e evidência próprios) e a trilha de correções (valor antigo → novo,
 * autor e hora).
 */
const OpportunityPanel: React.FC<Props> = ({ sectorId, contactId, cardId, columns, onChanged, onOpenDossier, addToast }) => {
  const [opportunities, setOpportunities] = useState<ContactOpportunity[]>([]);
  const [origin, setOrigin] = useState<ContactOriginInfo | null>(null);
  const [history, setHistory] = useState<FieldChange[]>([]);
  const [identities, setIdentities] = useState<ContactIdentitiesInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<number | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftValue, setDraftValue] = useState('');
  const [draftCurrency, setDraftCurrency] = useState('BRL');
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newColumn, setNewColumn] = useState('');
  const [newValue, setNewValue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [opps, orig, hist, ids] = await Promise.all([
        listContactOpportunities(sectorId, contactId).catch(() => []),
        getContactOrigin(sectorId, contactId),
        listFieldChanges(sectorId, contactId).catch(() => []),
        Promise.resolve()
          .then(() => getContactIdentities(sectorId, contactId))
          .catch(() => null),
      ]);
      setOpportunities(opps);
      setOrigin(orig);
      setIdentities(ids ?? null);
      setHistory(hist);
    } finally {
      setLoading(false);
    }
  }, [sectorId, contactId]);

  useEffect(() => {
    load();
  }, [load]);

  const stageColumns = (columns ?? []).filter(c => c.kind !== 'ganho' && c.kind !== 'perdido');

  const startEdit = (o: ContactOpportunity) => {
    setEditing(o.id);
    setDraftTitle(o.title ?? '');
    setDraftValue(o.estimatedValue != null ? String(o.estimatedValue) : '');
    setDraftCurrency(o.currency ?? 'BRL');
  };

  const saveEdit = async (o: ContactOpportunity) => {
    const trimmed = draftValue.trim().replace(',', '.');
    const value = trimmed === '' ? null : Number(trimmed);
    if (value != null && (!Number.isFinite(value) || value < 0)) {
      addToast?.('Valor inválido.', 'error');
      return;
    }
    setBusy(true);
    try {
      const res = await updateOpportunity(o.id, {
        title: draftTitle,
        estimatedValue: value,
        currency: value == null ? null : draftCurrency,
        clearValue: value == null && o.estimatedValue != null,
      });
      addToast?.(res.changed > 0 ? 'Correção registrada no histórico.' : 'Nada mudou.', 'success');
      setEditing(null);
      await load();
      if (res.changed > 0) onChanged?.();
    } catch {
      addToast?.('Não foi possível salvar a correção.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveTemperature = async (o: ContactOpportunity, raw: string) => {
    const next: Temperature | null = normalizeTemperature(raw);
    if (next === (o.temperature ?? null)) return;
    setBusy(true);
    try {
      const res = await setOpportunityTemperature(o.id, next);
      addToast?.(res.changed > 0 ? 'Temperatura corrigida (fica no histórico).' : 'Nada mudou.', 'success');
      await load();
      if (res.changed > 0) onChanged?.();
    } catch {
      addToast?.('Não foi possível corrigir a temperatura.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const submitNew = async () => {
    if (!newTitle.trim()) {
      addToast?.('Informe o assunto da oportunidade.', 'error');
      return;
    }
    const trimmed = newValue.trim().replace(',', '.');
    const value = trimmed === '' ? null : Number(trimmed);
    if (value != null && (!Number.isFinite(value) || value < 0)) {
      addToast?.('Valor inválido.', 'error');
      return;
    }
    setBusy(true);
    try {
      await createOpportunity({
        sectorId,
        contactId,
        columnId: newColumn ? Number(newColumn) : null,
        title: newTitle.trim(),
        estimatedValue: value,
        currency: value == null ? null : 'BRL',
      });
      addToast?.('Oportunidade criada no funil.', 'success');
      setCreating(false);
      setNewTitle('');
      setNewValue('');
      setNewColumn('');
      await load();
      onChanged?.();
    } catch {
      addToast?.('Não foi possível criar a oportunidade.', 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading && opportunities.length === 0) {
    return <div className="opp-panel opp-panel--loading">Carregando oportunidades…</div>;
  }

  return (
    <div className="opp-panel" data-testid="opportunity-panel">
      <section className="opp-panel__section">
        <h3><FiMapPin aria-hidden /> Origem e canais</h3>
        <dl className="opp-panel__facts">
          <dt>Origem original</dt>
          <dd data-testid="origin-original">
            {origin ? origin.label : 'Sem registro'}
            {origin?.campaign && <span className="opp-panel__muted"> · {origin.campaign}</span>}
            {origin?.firstSeenAtUtc && <span className="opp-panel__muted"> · {formatWhen(origin.firstSeenAtUtc)}</span>}
          </dd>
          {origin?.detail && (
            <>
              <dt>Detalhe</dt>
              <dd className="opp-panel__muted">{origin.detail}</dd>
            </>
          )}
          <dt>Canal atual</dt>
          <dd data-testid="origin-current">{origin?.currentChannel ?? '—'}</dd>
          <dt>Canais conhecidos</dt>
          <dd data-testid="origin-known">
            {identities && identities.identities.length > 0 ? (
              <ContactIdentityList identities={identities.identities} events={identities.events} />
            ) : origin && origin.knownChannels.length > 0 ? (
              origin.knownChannels.join(', ')
            ) : (
              '—'
            )}
          </dd>
        </dl>
      </section>

      <LeadFormAnswers sectorId={sectorId} contactId={contactId} />

      <section className="opp-panel__section">
        <div className="opp-panel__head">
          <h3><FiGitBranch aria-hidden /> Oportunidades deste cliente</h3>
          {!creating && (
            <button type="button" className="opp-panel__link" onClick={() => setCreating(true)}>
              <FiPlus aria-hidden /> Nova oportunidade
            </button>
          )}
        </div>
        <p className="opp-panel__hint">Cada oportunidade é um card do funil, com etapa, valor e evidência próprios.</p>
        <ul className="opp-panel__list">
          {opportunities.map(o => (
            <li key={o.id} className={`opp-item ${o.id === cardId ? 'opp-item--current' : ''}`} data-testid={`opportunity-${o.id}`}>
              {editing === o.id ? (
                <div className="opp-item__edit">
                  <label>
                    Assunto
                    <input value={draftTitle} maxLength={80} onChange={e => setDraftTitle(e.target.value)} />
                  </label>
                  <div className="opp-item__value-row">
                    <label>
                      Valor estimado
                      <input inputMode="decimal" value={draftValue} onChange={e => setDraftValue(e.target.value)} placeholder="A definir" />
                    </label>
                    <label>
                      Moeda
                      <select value={draftCurrency} onChange={e => setDraftCurrency(e.target.value)}>
                        <option value="BRL">BRL</option>
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                      </select>
                    </label>
                  </div>
                  <div className="opp-item__actions">
                    <button type="button" className="sidebar-button secondary" onClick={() => setEditing(null)} disabled={busy}>
                      <FiX aria-hidden /> Cancelar
                    </button>
                    <button type="button" className="sidebar-button primary" onClick={() => saveEdit(o)} disabled={busy}>
                      <FiSave aria-hidden /> Salvar correção
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="opp-item__top">
                    <strong>{o.title || (o.isEntry ? 'Contato' : 'Sem assunto')}</strong>
                    <span className={`opp-item__status opp-item__status--${o.isClosed ? o.columnKind : o.isEntry ? 'entrada' : 'aberta'}`}>
                      {statusLabel(o)}
                    </span>
                  </div>
                  <div className="opp-item__meta">
                    <span>Etapa: {o.columnName ?? '—'}</span>
                    <span>Valor: {formatMoney(o.estimatedValue, o.currency)}</span>
                    {o.id === cardId && <span className="opp-item__here">neste card</span>}
                  </div>
                  {o.lastMove?.evidence && (
                    <div className="opp-item__evidence">“{o.lastMove.evidence}”</div>
                  )}
                  {!o.isEntry && (
                    <div className="opp-item__temperature" data-testid={`opportunity-temperature-${o.id}`}>
                      <label>
                        <span>Temperatura</span>
                        <select
                          aria-label="Temperatura da oportunidade"
                          value={o.temperature ?? ''}
                          disabled={busy}
                          onChange={e => void saveTemperature(o, e.target.value)}
                        >
                          <option value="">Sem temperatura</option>
                          <option value="quente">Quente</option>
                          <option value="morna">Morna</option>
                          <option value="fria">Fria</option>
                        </select>
                      </label>
                      <TemperatureChip temperature={o.temperature} evidence={o.temperatureEvidence} source={o.temperatureSource} compact />
                      {o.temperature && o.temperatureSource === 'ai' && o.temperatureEvidence && (
                        <span className="opp-panel__muted opp-item__temperature-why">IA: “{o.temperatureEvidence}”</span>
                      )}
                      {o.temperature && o.temperatureSource !== 'ai' && (
                        <span className="opp-panel__muted opp-item__temperature-why">Corrigida à mão — a IA só volta a ler quando o cliente escrever de novo.</span>
                      )}
                    </div>
                  )}
                  <div className="opp-item__foot">
                    <span className="opp-panel__muted">
                      {CREATOR_LABELS[o.createdBy ?? ''] ?? 'card'} · {formatWhen(o.createdAt)}
                    </span>
                    <span className="opp-item__links">
                      {onOpenDossier && (
                        <button type="button" className="opp-panel__link" onClick={() => onOpenDossier(o.id)}>
                          <FiExternalLink aria-hidden /> Abrir ficha
                        </button>
                      )}
                      {!o.isClosed && (
                        <button
                          type="button"
                          className="opp-panel__link"
                          onClick={() => openRegisterConversation({
                            contactId,
                            opportunityId: o.id,
                            sourceSurface: 'oportunidade',
                            screen: 'funnel',
                          })}
                        >
                          <FiMic aria-hidden /> Registrar
                        </button>
                      )}
                      <button type="button" className="opp-panel__link" onClick={() => startEdit(o)}>
                        <FiEdit2 aria-hidden /> Corrigir
                      </button>
                    </span>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
        {creating && (
          <div className="opp-item opp-item__edit opp-panel__new" data-testid="new-opportunity-form">
            <label>
              Assunto (produto ou serviço)
              <input value={newTitle} maxLength={80} onChange={e => setNewTitle(e.target.value)} placeholder="Ex.: Manutenção anual" />
            </label>
            <div className="opp-item__value-row">
              <label>
                Etapa
                <select value={newColumn} onChange={e => setNewColumn(e.target.value)}>
                  <option value="">Primeira etapa</option>
                  {stageColumns.map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </label>
              <label>
                Valor (R$)
                <input inputMode="decimal" value={newValue} onChange={e => setNewValue(e.target.value)} placeholder="A definir" />
              </label>
            </div>
            <div className="opp-item__actions">
              <button type="button" className="sidebar-button secondary" onClick={() => setCreating(false)} disabled={busy}>
                <FiX aria-hidden /> Cancelar
              </button>
              <button type="button" className="sidebar-button primary" onClick={submitNew} disabled={busy}>
                <FiPlus aria-hidden /> Criar oportunidade
              </button>
            </div>
          </div>
        )}
      </section>

      <section className="opp-panel__section">
        <h3><FiRotateCcw aria-hidden /> Correções</h3>
        {history.length === 0 ? (
          <p className="opp-panel__muted">Nenhuma correção registrada.</p>
        ) : (
          <ul className="opp-panel__history" data-testid="field-history">
            {history.map(h => {
              const opp = h.cardId ? opportunities.find(o => o.id === h.cardId) : null;
              return (
                <li key={h.id}>
                  <div className="opp-panel__history-what">
                    <strong>{FIELD_LABELS[h.field] ?? h.field}</strong>
                    {h.entity === 'opportunity' && opp?.title ? <span className="opp-panel__muted"> · {opp.title}</span> : null}
                    {h.entity === 'contact' ? <span className="opp-panel__muted"> · contato</span> : null}
                  </div>
                  <div>
                    <span className="opp-panel__old">{historyValue(h.field, h.oldValue)}</span> → <span>{historyValue(h.field, h.newValue)}</span>
                  </div>
                  <div className="opp-panel__muted">
                    {h.actorName ?? (h.actorKind === 'ai' ? 'IA' : h.actorKind === 'user' ? 'Usuário' : 'Sistema')} · {formatWhen(h.createdAt)}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default OpportunityPanel;
